import { LitElement, html, css } from 'lit';
import './Tooltip.js';
import './MessageBox.js';
import { FetchMixin } from '../FetchMixin.js';
import alertStore from '../AlertStore.js';
import { setAlert } from '../AlertSlice.js';

class DashboardAppAdSetting extends FetchMixin(LitElement) {
  static get styles() {
    return css`
      :host {
        width: 100%;
        height: 100%;
        display: none;
        left: 50%;
        top: 50%;
        transform: translateX(-50%) translateY(-50%);
        position: fixed;
        background: rgba(0, 0, 0, 0.6);
        z-index: 1000;
        user-select: none;
      }

      :host([visible]) {
        display: block;
      }

      .component-content {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .ad-setting-content {
        width: 300px;
        background: white;
        border-radius: 10px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        padding: 20px;
        display: none;
        flex-direction: column;
        align-items: center;
        gap: 20px;
      }

      .ad-setting-title {
        font-size: 18px;
        font-weight: bold;
      }

      .ad-setting-description {
        font-size: 14px;
        color: #555;
        text-align: center;
      }

      .ad-setting-button,
      .confirm-ok-button {
        background-color: #22c72d;
        color: white;
        border: none;
        border-radius: 4px;
        padding: 10px 20px;
        cursor: pointer;
        font-size: 14px;
        text-align: center;
      }

      .ad-setting-button:hover,
      .confirm-ok-button:hover {
        background-color: #199121;
      }
      
      .confirm-cancel-button {
        background-color: #f8f9fa;
        border: none;
        border-radius: 4px;
        padding: 10px 20px;
        cursor: pointer;
        font-size: 14px;
        text-align: center;
      }

      .confirm-cancel-button:hover {
        background-color: #e2e6ea;
      }

      .ad-setting-button:disabled,
      .confirm-ok-button:disabled {
        background-color: #bdbdbd;
        color: black;
        cursor: not-allowed;
      }
      
      .ad-setting-options {
        width: 100%;
        display: flex;
        flex-direction: row;
        gap: 10px;
        align-items: center;
        justify-content: center;
      }

      .switch {
        position: relative;
        display: inline-block;
        width: 60px;
        height: 34px;
      }

      .switch input { 
        opacity: 0;
        width: 0;
        height: 0;
      }

      .slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #ccc;
        -webkit-transition: .4s;
        transition: .4s;
        border-radius: 34px;
      }

      .slider:before {
        position: absolute;
        content: "";
        height: 26px;
        width: 26px;
        left: 4px;
        bottom: 4px;
        background-color: white;
        -webkit-transition: .4s;
        transition: .4s;
        border-radius: 50%;
      }

      input:checked + .slider {
        background-color: #22c72d;
      }

      input:focus + .slider {
        box-shadow: 0 0 1px #22c72d;
      }

      input:checked + .slider:before {
        -webkit-transform: translateX(26px);
        -ms-transform: translateX(26px);
        transform: translateX(26px);
      }

      .ad-setting-option {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .ad-setting-option label {
        font-size: 14px;
        color: #333;
      }

      .ad-setting-end-content {
        gap: 10px;
      }

      .ad-setting-end-date,
      .ad-setting-end-byte  {
        font-size: 14px;
        color: #44892e;
      }
    `;
  }

  // Properties remain unchanged
  static get properties() {
    return {
      identKey: { type: String },
      appKey: { type: String },
      visible: { type: Boolean },
      autoExtend: { type: Boolean },
      startDate: { type: String },
      endDate: { type: String },
      maxMb: { type: Number },
      currentMb: { type: Number },
      adSubscription: { type: Object },
    };
  }

  // updated method remains unchanged
  updated(changedProperties) {
    if (changedProperties.has('visible')) {
      this.visible ? this.setAttribute('visible', '') : this.removeAttribute('visible');
    }
  }

  // Constructor remains unchanged
  constructor() {
    super();
    this.visible = false;
    this.autoExtend = false;
    this.startDate = this.calculateAdStartDate();
    this.endDate = this.calculateAdEndDate();
    this.maxMb = 0;
    this.currentMb = 0;
    this.adSubscription = undefined;

  }

  // firstUpdated method remains unchanged
  firstUpdated() {
    const fontAwasomeStyle = document.querySelector(
      "link[rel='stylesheet'][data-name='fontawesomev6']"
    );
    const fontAwasomeStyleClone = fontAwasomeStyle.cloneNode(true);
    fontAwasomeStyleClone.removeAttribute('disabled');
    this.shadowRoot.insertBefore(fontAwasomeStyleClone, this.shadowRoot.firstChild);

    this.fetchGetPwappyAdSubscription();
  }

  calculateAdStartDate() {
    const today = new Date();
    // 日付をYYYY/MM/DD形式で返す
    return `${today.getFullYear()}/${today.getMonth() + 1}/${today.getDate()}`;
  }

  calculateAdEndDate() {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 31);
    // 日付をYYYY/MM/DD形式で返す
    return `${endDate.getFullYear()}/${endDate.getMonth() + 1}/${endDate.getDate()}`;
  }

  // show method remains unchanged
  async show() {
    this.visible = true;
  }

  // hide method remains unchanged
  hide() {
    this.visible = false;
    this.remove();
  }

  showAdSetting() {
    // ad-setting-confirmを表示
    const adSettingConfirm = this.shadowRoot.querySelector('#ad-setting-confirm');
    adSettingConfirm.show();
  }

  // PPを取得する
  async fetchGetPwappyAdSubscription() {

    let endpoint = process.env.AD_SUBSCRIPTION_ENDPOINT;
    endpoint = endpoint + '?appKey=' + this.appKey
    const options = {
      method: 'GET',
    };

    const json = await this.fetchWithAuth(endpoint, options);
    //json.codeが200以外の場合
    if (json.code !== 200) {
      return Promise.reject(json);
    }
    this.adSubscription = json.details.adSubscription;
    if (this.adSubscription) {
      //this.adSubscription.autoExtendが"0"ならfalse
      this.autoExtend = this.adSubscription.autoExtend;
      this.startDate = this.adSubscription.startDate.split(' ')[0].replaceAll('-', '/');
      this.endDate = this.adSubscription.endDate.split(' ')[0].replaceAll('-', '/');
      this.maxMb = Number(this.adSubscription.maxMb);
      this.currentMb = Number(this.adSubscription.currentMb);
    }

    const content = this.shadowRoot.querySelector('.ad-setting-content');
    content.style.display = 'flex';

    return Promise.resolve(json);
  }

  // PPを使用する
  async fetchPostPwappyAdSubscription() {

    const endpoint = process.env.AD_SUBSCRIPTION_ENDPOINT;

    const options = {
      method: 'POST',
      body: JSON.stringify({ appKey: this.appKey, autoExtend: this.autoExtend })
    };

    const json = await this.fetchWithAuth(endpoint, options);
    //json.codeが200以外の場合
    if (json.code !== 200) {
      return Promise.reject(json);
    }

    this.adSubscription = json.details.adSubscription;
    if (this.adSubscription) {
      this.autoExtend = this.adSubscription.autoExtend;
      this.startDate = this.adSubscription.startDate.split(' ')[0].replaceAll('-', '/');
      this.endDate = this.adSubscription.endDate.split(' ')[0].replaceAll('-', '/');
      this.maxMb = Number(this.adSubscription.maxMb);
      this.currentMb = Number(this.adSubscription.currentMb);
    }
    return Promise.resolve(json);
  }

  // 自動継続の切り替え
  async fetchPutPwappyAdSubscription() {

    const endpoint = process.env.AD_SUBSCRIPTION_ENDPOINT;

    const options = {
      method: 'PUT',
      body: JSON.stringify({ appKey: this.appKey, autoExtend: this.autoExtend })
    };

    const json = await this.fetchWithAuth(endpoint, options);
    //json.codeが200以外の場合
    if (json.code !== 200) {
      return Promise.reject(json);
    }

    this.adSubscription = json.details.adSubscription;
    if (this.adSubscription) {
      this.autoExtend = this.adSubscription.autoExtend;
      this.startDate = this.adSubscription.startDate.split(' ')[0].replaceAll('-', '/');
      this.endDate = this.adSubscription.endDate.split(' ')[0].replaceAll('-', '/');
      this.maxMb = Number(this.adSubscription.maxMb);
      this.currentMb = Number(this.adSubscription.currentMb);
    }
    return Promise.resolve(json);
  }
  /**
   * ポイントを使用する
   */
  async pointUse(event) {
    try{
      let ret = await this.fetchPostPwappyAdSubscription();
      window.dispatchEvent(new Event('visibilitychange'));
    }catch(e){
      console.log(e.message);
      alertStore.dispatch(setAlert(e.message));
    }
  }

  autoExtendChange(event) {
    this.autoExtend = event.target.checked;
    if (this.adSubscription) {
      this.fetchPutPwappyAdSubscription();
    }
  }


  render() {
    return html`

      <message-box id="ad-setting-confirm">
        <div slot="message-slot">
            <p style="text-align: center;">本当に使用しますか？</p>
            <p style="text-align: center; font-size:12px;">(使用するを押すとキャンセルできません)</p>
        </div>
        <button slot="ok-slot" class="confirm-ok-button" @click="${this.pointUse}">使用する</button>
        <button slot="cancel-slot" class="confirm-cancel-button">キャンセル</button>
      </message-box>

      <div class="component-content" @click="${this.hide}">
        <div class="ad-setting-content" @click="${e => e.stopPropagation()}">
          <div class="ad-setting-title">広告設定</div>
          ${this.adSubscription !== undefined ? '' : html`<div class="ad-setting-description">1,000PPで1,000MByteまでのアウトプットまたは、30日の間広告を非表示にできます</div>`}
          ${this.adSubscription === undefined ?
        html`<button class="ad-setting-button" @click="${this.showAdSetting}">1,000PPを使用</button>` :
        html`<button class="ad-setting-button" disabled>ポイント使用済み</button>`
      }
          <div class="ad-setting-end-content">
            <div class="ad-setting-end-date">広告非表示期間: ${this.autoExtend ? this.startDate + ' 〜 ' + 'PPが続くまで' : this.startDate + ' 〜 ' + this.endDate}</div>
            ${this.adSubscription === undefined ? '' : html`<div class="ad-setting-end-byte">使用Byte: ${this.currentMb}/${this.maxMb.toLocaleString()} MByte (${this.currentMb === 0 ? 0 : this.currentMb / this.maxMb * 100} %)<tooltip-element text="前日までに使用したアウトプットのByteです"></tooltip-element></div>`}
          </div>
          <div class="ad-setting-options">
            <label class="switch">
              <input type="checkbox" .checked="${this.autoExtend}" @change="${this.autoExtendChange}">
              <span class="slider"></span>
            </label>
            <span>自動継続<tooltip-element text="有効にすると、広告を非表示にする機能をPPが続く限り継続します"></tooltip-element></span>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('dashboard-app-ad-setting', DashboardAppAdSetting);
